import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import useApiGet from "../hooks/getData";

/**
 * Lists live rooms whose host is currently inside (GET tournaments/admin/available-rooms).
 * Busy rooms are shown but disabled, with the reason from the backend.
 *
 * Props:
 *  - selected: array of room_id strings
 *  - onChange(ids)
 *  - excludeRoomIds: room ids to hide (e.g. already in the tournament)
 */
function RoomPicker({ selected, onChange, excludeRoomIds = [] }) {
  const { getData } = useApiGet();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData("/tournaments/admin/available-rooms");
      setRooms(res?.data?.rooms || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load rooms.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const visible = rooms.filter((r) => !excludeRoomIds.map(String).includes(String(r.room_id)));

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  return (
    <div className="pt-1">
      <div className="flex items-center justify-between pb-1">
        <span className="text-xs font-poppins text-[#666666] dark:text-darkText">
          Only live rooms with the host inside are listed.
        </span>
        <button
          type="button"
          onClick={fetchRooms}
          disabled={loading}
          className="px-3 py-1 text-xs font-poppins text-[#593A99] border border-[#593A99] rounded-lg disabled:opacity-40"
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="overflow-y-auto border max-h-72 border-opacity-gradient dark:border-[#1F1F1F] rounded-lg">
        {visible.length === 0 ? (
          <p className="p-4 text-sm text-center font-poppins dark:text-darkText">
            {loading ? "Loading..." : "No live rooms with a host inside right now."}
          </p>
        ) : (
          visible.map((r) => {
            const disabled = !r.is_available;
            const checked = selected.includes(r.room_id);
            return (
              <label
                key={r.room_id}
                className={`flex items-center gap-3 p-2.5 border-b last:border-b-0 dark:border-[#1F1F1F] font-poppins text-sm dark:text-darkText ${
                  disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={checked}
                  onChange={() => toggle(r.room_id)}
                />
                {r.host_avatar ? (
                  <img
                    src={r.host_avatar}
                    alt=""
                    className="object-cover w-8 h-8 rounded-full"
                    onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#D5CCE8]" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.room_name || `Room ${r.room_id}`}</div>
                  <div className="text-xs text-[#666666] dark:text-darkText truncate">
                    Host: {r.host_name || r.host_user_id} · Room ID {r.room_id}
                  </div>
                </div>
                <span className="text-xs">{r.viewer_count} viewers</span>
                {disabled && (
                  <span className="text-xs text-[#D92D20]">{r.busy_reason}</span>
                )}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

export default RoomPicker;
